import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CalendarCheck, CalendarDays, Camera, ChevronLeft, ChevronRight, Clock3,
  Gift, Heart, Mail, MapPin, Menu, Music2, Pause, Play, Smile, X,
} from 'lucide-react';
import { useCountdown } from '@/hooks/useCountdown';
import { useMusicPlayer } from '@/context/MusicPlayerContext';

const menu = [
  ['INÍCIO', 'inicio', null], ['A FESTA', 'festa', null], ['LOCAL', 'local', null], ['DRESS CODE', 'traje', null],
  ['CADERNO DE RECADOS', 'recados', '/caderno-de-recados'], ['LISTA DE PRESENTES', 'presentes', '/lista-de-presentes'], ['GALERIA', 'galeria', '/galeria'],
] as const;

function Title({ children }: { children: React.ReactNode }) {
  return <div className="section-title"><i /><h2>{children}</h2><i /><Heart size={12} fill="currentColor" /></div>;
}

const SITE_ENTERED_KEY = 'valentina-site-entered';

export default function Home() {
  const location = useLocation();
  const navigationState = location.state as { skipInvitation?: boolean; forceInvitation?: boolean } | null;
  const skipInvitation = Boolean(navigationState?.skipInvitation);
  const forceInvitation = Boolean(navigationState?.forceInvitation);
  const alreadyEntered = forceInvitation ? false : skipInvitation || sessionStorage.getItem(SITE_ENTERED_KEY) === '1';
  const [invitationOpened, setInvitationOpened] = useState(false);
  const [invitationZoomed, setInvitationZoomed] = useState(false);
  const [siteEntered, setSiteEntered] = useState(alreadyEntered);
  const [invitationClosing, setInvitationClosing] = useState(false);
  const time = useCountdown('2026-10-17T21:00:00-03:00');
  const [slide, setSlide] = useState(0);
  const [galleryPreview, setGalleryPreview] = useState<number | null>(null);
  const [galleryHovered, setGalleryHovered] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const galleryTrackRef = useRef<HTMLDivElement>(null);
  const { isPlaying, musicTime, musicDuration, toggleMusic, seekMusic, play, pause, requestAutoplay, setInvitationVisible } = useMusicPlayer();
  const [isMobile, setIsMobile] = useState(false);
  const gallery = Array.from({ length: 20 }, (_, index) => index + 1);
  const move = (direction: number) => setSlide((slide + direction + gallery.length) % gallery.length);

  const enterSite = () => {
    if (invitationClosing) return;
    play();
    setInvitationClosing(true);
    window.setTimeout(() => setSiteEntered(true), 950);
  };

  const backToInvitation = () => {
    pause();
    setSiteEntered(false);
    setInvitationClosing(false);
    setInvitationZoomed(false);
    setInvitationOpened(false);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    document.body.classList.toggle('invitation-active', !siteEntered);
    return () => document.body.classList.remove('invitation-active');
  }, [siteEntered]);

  useLayoutEffect(() => {
    setInvitationVisible(!siteEntered);
    if (siteEntered) sessionStorage.setItem(SITE_ENTERED_KEY, '1');
    else sessionStorage.removeItem(SITE_ENTERED_KEY);
    return () => setInvitationVisible(false);
  }, [siteEntered, setInvitationVisible]);

  useEffect(() => {
    // Se o site já tinha sido aberto nesta aba (F5 ou navegação direta), retoma a música.
    if (alreadyEntered) requestAutoplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 700px)');
    const updateDevice = () => setIsMobile(media.matches);
    updateDevice();
    media.addEventListener('change', updateDevice);
    return () => media.removeEventListener('change', updateDevice);
  }, []);

  useEffect(() => {
    const track = galleryTrackRef.current;
    const item = track?.children[slide] as HTMLElement | undefined;
    if (track && item) track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
  }, [slide]);

  useEffect(() => {
    if (galleryHovered || galleryPreview !== null) return;
    const timer = window.setInterval(() => setSlide((value) => (value + 1) % gallery.length), 3200);
    return () => window.clearInterval(timer);
  }, [galleryHovered, galleryPreview, gallery.length]);

  useEffect(() => {
    document.body.classList.toggle('gallery-modal-open', galleryPreview !== null);
    const onKey = (event: KeyboardEvent) => {
      if (galleryPreview === null) return;
      if (event.key === 'Escape') setGalleryPreview(null);
      if (event.key === 'ArrowLeft') setGalleryPreview((galleryPreview + gallery.length - 1) % gallery.length);
      if (event.key === 'ArrowRight') setGalleryPreview((galleryPreview + 1) % gallery.length);
    };
    window.addEventListener('keydown', onKey);
    return () => { document.body.classList.remove('gallery-modal-open'); window.removeEventListener('keydown', onKey); };
  }, [galleryPreview, gallery.length]);

  useEffect(() => {
    const animatedSections = document.querySelectorAll<HTMLElement>('.content > .block, .content > .gift-button, .content > footer');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    animatedSections.forEach((element) => {
      element.classList.add('reveal-on-scroll');
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  const directionsUrl = isMobile
    ? 'https://www.waze.com/ul?q=Rua%20Alvarenga%201083%2C%20Butant%C3%A3%2C%20SP&navigate=yes'
    : 'https://www.google.com/maps/search/?api=1&query=Rua+Alvarenga+1083%2C+Butant%C3%A3%2C+SP';

  return (
    <main className={`site-shell ${invitationClosing ? 'site-is-revealing' : ''} ${siteEntered ? 'site-is-entered' : ''}`}>
      {!siteEntered && (
        <section className={`invitation-intro ${invitationOpened ? 'is-open' : ''} ${invitationZoomed ? 'is-zoomed' : ''} ${invitationClosing ? 'is-closing' : ''}`} aria-label="Convite de quinze anos da Valentina">
          <div className="invitation-glow" aria-hidden="true" />
          <div className="invitation-stage">
            <div
              className="invitation-card-wrap"
              role="button"
              tabIndex={invitationOpened ? 0 : -1}
              aria-label="Ampliar convite"
              onClick={() => invitationOpened && setInvitationZoomed(true)}
              onKeyDown={(event) => {
                if (invitationOpened && (event.key === 'Enter' || event.key === ' ')) setInvitationZoomed(true);
              }}
            >
              <img className="invitation-card" src="/convite-valentina.png" alt="Convite da festa de quinze anos da Valentina" />
            </div>
            <button
              className="invitation-full-close"
              type="button"
              aria-label="Fechar convite e voltar ao envelope"
              onClick={(event) => { event.stopPropagation(); setInvitationZoomed(false); }}
            >
              <X />
            </button>
            <button
              className="envelope"
              type="button"
              aria-label="Abrir convite"
              aria-expanded={invitationOpened}
              onClick={() => setInvitationOpened(true)}
            >
              <img className="envelope-exact" src="/convite-envelope-exato.png" alt="" />
              <span className="envelope-hint">CLIQUE PARA ABRIR</span>
            </button>
            <button className="enter-site-button" type="button" onClick={enterSite} disabled={invitationClosing}>
              ACESSAR MEU SITE <Heart size={15} fill="currentColor" />
            </button>
          </div>
        </section>
      )}
      <section id="inicio" className="hero section-surface" aria-label="Valentina usando vestido de debutante">
        <div className="flower flower-top" aria-hidden="true" />
        <header className="topbar">
          <a className="mobile-topbar-brand" href="#inicio" aria-label="Valentina quinze anos — início">
            <strong>VALENTINA</strong><span>quinze anos</span>
          </a>
          <button
            className="mobile-menu-toggle"
            type="button"
            aria-label={mobileMenuOpen ? 'Fechar menu' : 'Abrir menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="main-navigation"
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
          <nav id="main-navigation" className={mobileMenuOpen ? 'is-open' : ''} aria-label="Menu principal">
            {menu.map(([label, id, to], index) => to
              ? <Link className={index === 0 ? 'active' : ''} to={to} onClick={() => setMobileMenuOpen(false)} key={id}>{label}</Link>
              : <a className={index === 0 ? 'active' : ''} href={`#${id}`} onClick={() => setMobileMenuOpen(false)} key={id}>{label}</a>)}
            <a className="topbar-invite-link" href="#" onClick={(event) => { event.preventDefault(); setMobileMenuOpen(false); backToInvitation(); }}>
              <Mail size={13} /> CONVITE
            </a>
          </nav>
        </header>

        <div className="music-player" aria-label="Player da música da Valentina">
          <Music2 size={17} />
          <span className="music-meta"><small>NOSSA MÚSICA</small><strong>Música da Valentina</strong></span>
          <input
            aria-label="Progresso da música"
            type="range"
            min="0"
            max={musicDuration}
            value={musicTime}
            onChange={(event) => seekMusic(Number(event.target.value))}
            style={{ '--music-progress': `${(musicTime / musicDuration) * 100}%` } as React.CSSProperties}
          />
          <button type="button" onClick={toggleMusic} aria-label={isPlaying ? 'Pausar música' : 'Tocar música'}>
            {isPlaying ? <Pause size={13} fill="currentColor" /> : <Play size={13} fill="currentColor" />}
          </button>
        </div>

        <div className="hero-copy">
          <h1>VALENTINA</h1>
          <p className="script">quinze anos</p>
          <Heart className="heart" size={18} fill="currentColor" />
          <blockquote>“A contagem regressiva começou!<br />Todo grande capítulo merece uma noite inesquecível.<br />E eu quero você comigo para viver a minha!”</blockquote>
          <div className="countdown" aria-label="Contagem regressiva">
            {[
              [time.days, 'DIAS'], [time.hours, 'HORAS'], [time.minutes, 'MINUTOS'], [time.seconds, 'SEGUNDOS'],
            ].map(([value, label]) => <div key={label}><strong>{String(value).padStart(2, '0')}</strong><span>{label}</span></div>)}
          </div>
          <div className="hero-cta-row">
            <a className="rsvp-button" href="https://assessoriavip.com.br/rsvpUnico/8323e1e0-442d-11f1-a86a-614ad27280ba" target="_blank" rel="noreferrer">
              <CalendarCheck size={18} /> CONFIRMAR PRESENÇA
            </a>
            <Link className="gift-button gift-button-hero-pair" to="/lista-de-presentes"><Gift size={18} /> VER LISTA DE PRESENTES</Link>
          </div>
        </div>
        <div className="flower flower-hero-right" aria-hidden="true" />
      </section>

      <section className="content section-surface">
        <div className="flower flower-content-left" aria-hidden="true" />
        <Link className="gift-button gift-button-standalone" to="/lista-de-presentes"><Gift size={30} /> VER LISTA DE PRESENTES</Link>

        <section id="festa" className="party block">
          <Title>A FESTA</Title>
          <div className="party-grid">
            <div className="date-card"><CalendarDays /><small>sábado</small><p>17 de outubro de 2026</p></div>
            <div className="time-card"><Clock3 /><p>21 HORAS</p></div>
          </div>
        </section>

        <section id="local" className="place block">
          <Title>LOCAL</Title>
          <div className="place-grid">
            <div className="address"><MapPin /><div><h3>ESPAÇO MILLA</h3><p>RUA ALVARENGA N 1083<br />BUTANTÃ, SP</p></div></div>
            <div className="map-wrap">
              <a className="map-link" href={directionsUrl} target="_blank" rel="noreferrer" aria-label={isMobile ? 'Abrir local no Waze' : 'Abrir local no Google Maps'}>
                <div className="map"><span /><MapPin fill="currentColor" /></div>
              </a>
              <a className="directions-button" href={directionsUrl} target="_blank" rel="noreferrer">
                {isMobile ? 'IR DE WAZE' : 'ABRIR NO GOOGLE MAPS'} <Smile size={18} />
              </a>
            </div>
            <div className="valet"><p>VALLET<br /><span>NO LOCAL</span></p></div>
          </div>
        </section>

        <section id="traje" className="dress-code block">
          <Title>DRESS CODE</Title>
          <div className="dress-grid">
            <div className="dress-option dress-option-feminino">
              <img src="/dress-code-feminino-alpha.png" alt="Sugestões de vestidos longos para o traje feminino" />
              <section><h3>FEMININO</h3><p>Traje social completo<br /><strong>Vestido longo</strong></p></section>
            </div>
            <i />
            <div className="dress-option dress-option-masculino">
              <img src="/dress-code-masculino-alpha.png" alt="Sugestões de ternos para o traje masculino" />
              <section><h3>MASCULINO</h3><p>Traje social completo<br /><strong>Terno e gravata</strong></p></section>
            </div>
          </div>
          <p className="dress-note">A cor rosa e seus tons são reservados exclusivamente para a debutante.</p>
        </section>

        <section
          className="gallery block"
          aria-label="Galeria da Valentina"
          onMouseEnter={() => setGalleryHovered(true)}
          onMouseLeave={() => setGalleryHovered(false)}
        >
          <button onClick={() => move(-1)} aria-label="Fotos anteriores"><ChevronLeft /></button>
          <div className="gallery-track" ref={galleryTrackRef}>
            {gallery.map((photo, index) => <button
              type="button"
              className={`gallery-photo ${index === slide ? 'chosen' : ''}`}
              style={{ backgroundImage: `url(/galeria-valentina/foto-${String(photo).padStart(2, '0')}.jpg)` }}
              onClick={() => { setSlide(index); setGalleryPreview(index); }}
              aria-label={`Ampliar foto ${index + 1}`}
              key={photo}
            />)}
          </div>
          <button onClick={() => move(1)} aria-label="Próximas fotos"><ChevronRight /></button>
          <div className="dots">{gallery.map((_, i) => <button aria-label={`Ir para foto ${i + 1}`} className={i === slide ? 'selected' : ''} onClick={() => setSlide(i)} key={i} />)}</div>
          <Link className="home-gallery-link" to="/galeria"><Camera size={18}/> VER GALERIA COMPLETA</Link>
        </section>

        <footer>
          <div className="organizer"><small>Organizado por</small><img className="curione-logo" src="/logo-curione.svg" alt="Curione" /><span>FESTAS E ASSESSORIA</span></div>
          <i /><Heart fill="currentColor" /><i />
          <div className="developer-brand"><small>Orgulhosamente desenvolvido por</small><img className="serra-logo" src="/logo-serra-consultoria-original.png" alt="Serra Consultoria" /></div>
        </footer>
        <div className="flower flower-footer-right" aria-hidden="true" />
      </section>

      {galleryPreview !== null && (
        <div
          className="home-gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Foto ${galleryPreview + 1}`}
          onMouseDown={(event) => { if (event.target === event.currentTarget) setGalleryPreview(null); }}
        >
          <button className="home-gallery-lightbox-close" onClick={() => setGalleryPreview(null)} aria-label="Fechar"><X /></button>
          <button
            className="home-gallery-lightbox-nav home-gallery-lightbox-prev"
            onClick={() => setGalleryPreview((galleryPreview + gallery.length - 1) % gallery.length)}
            aria-label="Foto anterior"
          ><ChevronLeft /></button>
          <img
            src={`/galeria-valentina/foto-${String(gallery[galleryPreview]).padStart(2, '0')}.jpg`}
            alt={`Ensaio de quinze anos da Valentina — foto ${galleryPreview + 1}`}
          />
          <button
            className="home-gallery-lightbox-nav home-gallery-lightbox-next"
            onClick={() => setGalleryPreview((galleryPreview + 1) % gallery.length)}
            aria-label="Próxima foto"
          ><ChevronRight /></button>
        </div>
      )}
    </main>
  );
}
